import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade5Zhuyin.css";

/** Shuffle for quizzes */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade5Zhuyin
 *  - Focus: Paragraph reading, short stories, and building literacy in Zhuyin
 *  - Single quiz pool => 25 questions
 */

const readingNotes = (
  <div className="g5z-reading-notes">
    <h4>Paragraph Reading & Short Stories in Zhuyin</h4>
    <p>
      By Grade 5, you can read short paragraphs entirely in Zhuyin. 
      This helps reinforce tone usage and comprehension before transitioning fully to characters.
    </p>
    <ul>
      <li>Practice 1–2 paragraph stories: ㄐㄧㄣ ㄊㄧㄢ ㄒㄧㄥ ㄑㄧˊ ㄧ..., etc.</li>
      <li>Look for new words and guess meaning from context.</li>
      <li>Focus on fluid reading: no pausing at each character; group syllables into words.</li>
      <li>Check dictionary references for unknown phrases—Zhuyin can help locate them quickly.</li>
    </ul>
  </div>
);

const zhuyinQuestions = [
  {
    question: "Reading short paragraphs in Zhuyin helps you...?",
    correctAnswer: "Develop fluency and tone accuracy before full character reading",
    options: [
      "Avoid learning characters entirely",
      "Only read single words with no context",
      "Develop fluency & tone accuracy prior to full characters"
    ]
  },
  {
    question: "A short story might begin: ㄐㄧㄣ ㄊㄧㄢ ㄉㄜ˙ ㄊㄧㄢ ㄑㄧˋ ㄏㄣˇ ㄏㄠˇ => 'Today’s weather is very good.' This is an example of?",
    correctAnswer: "Reading a narrative line with multiple syllables in Zhuyin",
    options: [
      "Only a single word repeated",
      "No actual meaning",
      "A narrative line in Zhuyin"
    ]
  },
  {
    question: "If you see 2–3 lines of text in Zhuyin, how to handle unknown words?",
    correctAnswer: "Sound them out, check context or dictionary to confirm meaning",
    options: [
      "Skip them entirely",
      "They must be guessed arbitrarily",
      "Sound them out & check dictionary or context"
    ]
  },
  {
    question: "When reading a 'short story' in Zhuyin, why is grouping syllables into words important?",
    correctAnswer: "Mandarin is multi-syllabic; correct grouping helps meaning & flow",
    options: [
      "We always read character by character with big pauses",
      "Zhuyin can only read single syllables in isolation",
      "Mandarin often uses multi-syllable words, grouping helps flow"
    ]
  },
  {
    question: "Which approach fosters better comprehension when reading paragraphs?",
    correctAnswer: "Read aloud or silently in phrases, not character by character",
    options: [
      "Always read each character separately, no grouping",
      "Skip any multi-syllable words",
      "Read aloud in phrases for better sense & flow"
    ]
  },
  {
    question: "If the text says ㄧ ㄓ ㄍㄡˇ ㄏㄨㄟˊ ㄐㄧㄚ => 'A dog goes home.' We see measure word ㄓ for dog, and the verb ㄏㄨㄟˊ means?",
    correctAnswer: "Return or go back (home)",
    options: [
      "Eat or drink",
      "Live or remain",
      "Return or go back"
    ]
  },
  {
    question: "A reading passage might have ㄏㄠˇ ㄐㄧˇ ㄍㄜˋ ㄗㄠˇ ㄕㄤˋ => 'several mornings...' showing?",
    correctAnswer: "Use of measure word ㄍㄜˋ (個) + time word, describing repeated events",
    options: [
      "No actual usage in Chinese",
      "It means 'I have no time at all'",
      "A repeated event over several mornings"
    ]
  },
  {
    question: "If you see a dialogue in Zhuyin with names, e.g. ㄨㄛˇ ㄉㄜ˙ ㄕㄜˇ ㄐㄧㄝˇ => 'my aunt' or 'my auntie.' Tones matter to distinguish?",
    correctAnswer: "Yes, ㄕㄜˇ (3rd) vs. ㄕㄜˊ might differ. So ensuring correct tone is crucial",
    options: [
      "No difference in meaning",
      "Zhuyin is tone-less",
      "Tones are crucial for clarity"
    ]
  },
  {
    question: "Reading a story: ㄏㄡˋ ㄌㄞˋ ㄊㄚ ㄗㄨㄛˋ ㄌㄜ˙ ㄧ ㄍㄜ˙ ㄉㄚˋ ㄐㄧㄝˊ ㄌㄨㄣˋ => means 'Later, he made a big conclusion'? This helps practice?",
    correctAnswer: "Linking event sequences with transition words, e.g. ㄏㄡˋ ㄌㄞˋ (後來)",
    options: [
      "No transitions used in Chinese",
      "Only single-syllable words exist",
      "Practice transitions & storyline in Zhuyin"
    ]
  },
  {
    question: "Which is good practice for Grade 5 Zhuyin reading?",
    correctAnswer: "Reading short paragraphs daily, checking unfamiliar words in a dictionary",
    options: [
      "Avoid paragraphs, only read lists",
      "Use Pinyin only",
      "Practice paragraphs daily & check dictionary"
    ]
  },
  {
    question: "A short story might end with ㄊㄚ ㄏㄣˇ ㄍㄠ ㄒㄧㄥ, 'He is very happy.' in Zhuyin. The phrase ㄏㄣˇ ㄍㄠ ㄒㄧㄥ => means?",
    correctAnswer: "Very happy",
    options: [
      "Not happy",
      "Tired",
      "Very happy"
    ]
  },
  {
    question: "If you see ㄉㄨㄛ ㄅㄢˋ ㄕˋ ㄧ ㄖㄣˊ ㄗㄞˋ ㄩㄣˊ => might be 'mostly one person is on a cloud'? This suggests reading context to interpret properly. True or false?",
    correctAnswer: "True, context helps interpret an odd phrase or poetic expression",
    options: [
      "False, context doesn’t matter",
      "True, we rely on context for meaning",
      "We only read words literally"
    ]
  },
  {
    question: "In paragraph reading, ㄔㄤˊ ㄔㄤˊ => 'often' or 'frequently.' The repeated character approach in Chinese means?",
    correctAnswer: "A repeated syllable intensifies or shows an adverb-like usage",
    options: [
      "No difference from single ㄔㄤ",
      "We cannot repeat syllables in Zhuyin",
      "A common pattern for adverbs or emphasis"
    ]
  },
  {
    question: "When reading ㄕㄨㄟˇ ㄍㄨㄛˇ in a short story => 'shuǐguǒ' (fruits). If you see ㄘㄞˋ ㄍㄨㄛˇ => would that be correct?",
    correctAnswer: "Likely no meaning, might be a mistake or different word. So checking context is key",
    options: [
      "Yes, same meaning",
      "No difference at all",
      "It’s likely an error or different meaning"
    ]
  },
  {
    question: "A short reading about family might say ㄅㄠˇ ㄓㄨㄥ ㄓㄚˊ ㄐㄧ => 'bǎozhòng zhá jī'? Could be partial nonsense or require context. This shows?",
    correctAnswer: "Sometimes partial phrases appear, check the bigger context to decode meaning",
    options: [
      "Zhuyin must be incorrect",
      "No short stories exist in Chinese",
      "We rely on context to interpret partial phrases"
    ]
  },
  {
    question: "Reading a paragraph with transition words like ㄕㄡ ㄒㄧㄢ (first), ㄖㄢˊ ㄏㄡˋ (then), ㄗㄨㄟˋ ㄏㄡˋ (finally) helps you practice?",
    correctAnswer: "Narrative flow in Chinese, essential for short story reading",
    options: [
      "No transitions needed",
      "Only used in formal writing, not stories",
      "Yes, they show sequence & help reading flow"
    ]
  },
  {
    question: "Some stories might have quotes, e.g. ㄊㄚ ㄕㄨㄛ => 'He says...' and direct speech. Why is it helpful in Grade 5?",
    correctAnswer: "Practicing dialogues in reading builds real conversation skill",
    options: [
      "It’s too advanced for Grade 5",
      "No use for dialogues in reading",
      "Dialogs enhance conversation skill & reading"
    ]
  },
  {
    question: "If a paragraph ends with ㄎㄜˇ ㄕˋ ㄊㄚ ㄏㄨㄟˋ ㄧ ㄍㄥ ㄏㄠˇ ㄉㄜ˙ ㄐㄧㄝˇ ㄈㄤˇ => 'But he found a better solution,' the ㄍㄥ indicates?",
    correctAnswer: "Comparative 'even more' or 'better' than before",
    options: [
      "A measure word for solutions",
      "No meaning at all",
      "Comparative meaning: 'even more' / 'better'"
    ]
  },
  {
    question: "Reading can help you guess if ㄍㄨㄢ ㄒㄧ might be 'guanxi' (relationship) or ㄍㄨㄢˇ ㄒㄧ (managing breathing)? This highlights?",
    correctAnswer: "Tone usage: ㄍㄨㄢ (1st/4th) vs. ㄒㄧ (1st/ neutral) changes meaning, context clarifies",
    options: [
      "Zhuyin lumps everything together",
      "No difference in those combos",
      "Yes, tone & context differentiate meaning"
    ]
  },
  {
    question: "Gradually, reading short stories with Zhuyin builds confidence for reading full characters. True or false?",
    correctAnswer: "True, it’s an excellent transitional method",
    options: [
      "False, it hinders character learning",
      "True, it helps transition to full characters",
      "No correlation"
    ]
  },
  {
    question: "Paragraph-level reading also includes cultural notes. E.g., ㄔㄨㄣ ㄐㄧㄝˊ => Chinese New Year. This fosters?",
    correctAnswer: "Cultural understanding + vocab in context",
    options: [
      "No cultural aspect in language",
      "Only mechanical reading of words",
      "Cultural understanding + vocab in context"
    ]
  },
  {
    question: "To say 'My mother often cooks fish' => ㄨㄛˇ ㄉㄜ˙ ㄇㄚ ㄇㄚ ㄔㄤˊ ㄗㄨㄛˋ ㄩˊ => shows which repeated word for 'often'?",
    correctAnswer: "ㄔㄤˊ ㄔㄤˊ or just ㄔㄤˊ if once. Possibly the repeated form for emphasis",
    options: [
      "We can’t repeat in Zhuyin",
      "No usage for 'often'",
      "Likely ㄔㄤˊ or ㄔㄤˊ ㄔㄤˊ for frequency"
    ]
  },
  {
    question: "Finally, a tip: read aloud or silently daily, and try summarizing the paragraph in your own words. Why?",
    correctAnswer: "It improves comprehension, memory, and tone practice",
    options: [
      "It wastes time",
      "Zhuyin reading is just for kids, no summary needed",
      "It strengthens comprehension, memory, and tone usage"
    ]
  }
];

export default function Grade5Zhuyin() {
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  // Timer
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Quick Quiz generator
  const generateQuiz = () => {
    const q = zhuyinQuestions[Math.floor(Math.random() * zhuyinQuestions.length)];
    setQuiz({
      question: q.question,
      correctAnswer: q.correctAnswer,
      options: shuffleArray([...q.options])
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  // Quick Quiz answer
  const handleAnswerClick = (ans) => {
    setSelectedAnswer(ans);
    if (quiz && ans === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Final Quiz => entire zhuyinQuestions
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(zhuyinQuestions);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g5chi-page">
      <h1 className="g5chi-title">Grade 5 Zhuyin: Paragraph Reading</h1>
      <p className="g5chi-description">
        Practice reading short paragraphs, building literacy & tone confidence!
      </p>

      {/* Lesson Content */}
      <div className="g5chi-lesson-content">
        <h2 className="g5chi-lesson-title">注音: Short Stories & Paragraphs</h2>
        <p className="g5chi-lesson-description">
          By Grade 5, you can handle brief stories, dialogues, and paragraphs entirely in Zhuyin. 
          This fosters comprehension before tackling full Chinese characters.
        </p>
        <p className="g5chi-lesson-example">
          <strong>Example:</strong> ㄐㄧㄣ ㄊㄧㄢ ㄉㄜ˙ ㄊㄧㄢ ㄑㄧˋ ㄏㄣˇ ㄏㄠˇ, 'Today’s weather is great.'
        </p>
        {readingNotes}
      </div>

      {/* Quizzes */}
      <div className="g5chi-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g5chi-quick-quiz-container">
          <h2 className="g5chi-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g5chi-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g5chi-quiz-question">{quiz.question}</p>
              <div className="g5chi-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g5chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g5chi-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g5chi-final-quiz-container">
          <h2 className="g5chi-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g5chi-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && finalQuizQuestions.length > 0 && (
            <div className="g5chi-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>
                Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
              </p>
              <p className="g5chi-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g5chi-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && (
                <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Link to="/ChineseSubject" className="g5chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
